import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import type { RenewalSummaryJobStatus } from '@domain/schemas/renewal-summary.schema';
import { AUDIT_LOG_KEY } from '@queries/fetchEntity';
import { RENEWAL_SUMMARY_KEY } from '@queries/fetchRenewalSummary';
import { useQueryClient } from '@tanstack/react-query';

import { SummaryReadyToast } from '../components/SummaryReadyToast';
import { JOB_IN_FLIGHT } from '../constants';

import { useRenewalSummaryQuery } from './useRenewalSummaryQuery';

interface UseRenewalSummaryStatusArgs {
  renewalId: string;
  /** Shown in the "Summary ready" toast. */
  renewalName?: string;
  enabled: boolean;
  /** No toast while the panel is open on the summary: the broker is already looking at it. */
  isPanelOpen: boolean;
  /** The toast's "View summary" button: the caller takes the broker to the panel. */
  onViewSummary: () => void;
}

export const SUMMARY_FAILED_MESSAGE = "Summary couldn't be created. Try again. If it keeps failing, contact support.";

/**
 * Header-level watch on the renewal's summary job, mounted for as long as the
 * contract is open — unlike the drawer, which unmounts when closed — so a job
 * started and then left alone still announces itself. Polls the same query
 * the panel reads while a job is in flight, and toasts once when a job it saw
 * running finishes. Jobs already finished on first load are not news.
 */
export function useRenewalSummaryStatus({
  renewalId,
  renewalName,
  enabled,
  isPanelOpen,
  onViewSummary,
}: UseRenewalSummaryStatusArgs) {
  const queryClient = useQueryClient();
  const { data } = useRenewalSummaryQuery(renewalId, enabled);
  const job = data?.job;

  // Read inside the status effect without re-running it when these change.
  // Declared first, so it has updated by the time the status effect runs.
  const latest = useRef({ isPanelOpen, onViewSummary, renewalName });
  useEffect(() => {
    latest.current = { isPanelOpen, onViewSummary, renewalName };
  });

  const seenStatuses = useRef(new Map<string, RenewalSummaryJobStatus>());
  useEffect(() => {
    if (!job) return;
    const previous = seenStatuses.current.get(job.id);
    seenStatuses.current.set(job.id, job.status);
    if (!previous || !JOB_IN_FLIGHT[previous] || JOB_IN_FLIGHT[job.status]) return;

    if (job.status === 'SUCCEEDED') {
      // The poll that saw SUCCEEDED may predate the summary row; fetch again. The new
      // Activity entry does not bump the contract's `updated_at`, so refetch that too.
      queryClient.invalidateQueries({ queryKey: RENEWAL_SUMMARY_KEY(renewalId) });
      queryClient.invalidateQueries({ queryKey: AUDIT_LOG_KEY(renewalId) });
      if (latest.current.isPanelOpen) return;
      toast.success(
        ({ closeToast }) => (
          <SummaryReadyToast
            renewalName={latest.current.renewalName}
            onView={() => {
              closeToast();
              latest.current.onViewSummary();
            }}
          />
        ),
        { autoClose: 8_000 },
      );
    } else if (job.status === 'FAILED') {
      toast.error(SUMMARY_FAILED_MESSAGE, { autoClose: 8_000 });
    }
  }, [job, queryClient, renewalId]);
}
